import { lazy } from 'react';

import { Navigate, Outlet, type RouteObject } from 'react-router-dom';

import { MainLayout } from '@/widgets/layouts';

import { PATHS } from '@/shared/constants/routes/paths';
import { IS_WHITE_LABEL } from '@/shared/environments';

import { BaseLayout } from '../../layouts';
import { HomeRedirect } from '../HomeRedirect';
import { LegacyRouteBackwardCompatibility } from '../LegacyRouteBackwardCompatibility';
import { SpaceGuard } from '../SpaceGuard';
import { SpaceIndexRoute } from '../SpaceIndexRoute';

const HomeHubPage = lazy(() => import('@/pages/HomeHubPage'));
const HomePage = lazy(() => import('@/pages/HomePage'));
const ProjectPage = lazy(() => import('@/pages/ProjectPage'));
const AdminPage = lazy(() => import('@/pages/AdminPage'));
const Customers = lazy(() => import('@/pages/AdminPage/components/Content/components/Customers'));
const CustomersFeedback = lazy(() => import('@/pages/AdminPage/components/Content/components/CustomersFeedback'));
const Survey = lazy(() => import('@/pages/AdminPage/components/Content/components/Survey'));
const AdminRecordings = lazy(() => import('@/pages/AdminPage/components/Content/components/Recordings'));
const AdminRecordingDetail = lazy(() => import('@/pages/AdminPage/components/Content/components/RecordingDetail'));
const AdminMLTasks = lazy(() => import('@/pages/AdminPage/components/Content/components/MLTasks'));
const AdminOnscreenJobs = lazy(() => import('@/pages/AdminPage/components/Content/components/OnscreenJobs'));
const AdminOnscreenJobDetail = lazy(() => import('@/pages/AdminPage/components/Content/components/OnscreenJobDetail'));
const PricingPage = lazy(() => import('@/pages/PricingPage'));
const AccountPage = lazy(() => import('@/pages/AccountPage'));
const CheckoutPage = lazy(() => import('@/pages/CheckoutPage'));
const VoicePresetsPage = lazy(() => import('@/pages/VoicePresetsPage'));
const GlossariesPage = lazy(() => import('@/pages/GlossariesPage'));
const FoldersPage = lazy(() => import('@/pages/FoldersPage'));
const RecordingPage = lazy(() => import('@/pages/RecordingPage'));
const RecordsPage = lazy(() => import('@/pages/RecordsPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

const mainRoutes: RouteObject[] = [
  {
    children: [
      {
        element: (
          <SpaceIndexRoute>
            <HomeHubPage />
          </SpaceIndexRoute>
        ),
        index: true,
      },
      {
        element: <HomePage />,
        path: PATHS.translation,
      },
      {
        element: <RecordsPage />,
        path: PATHS.records,
      },
      {
        element: <VoicePresetsPage />,
        path: PATHS.voices,
      },
      {
        element: <GlossariesPage />,
        path: PATHS.glossaries,
      },
      {
        children: [
          {
            element: <Navigate to={`../${PATHS.translation}`} replace />,
            index: true,
          },
          {
            element: <FoldersPage />,
            path: ':folderId',
          },
        ],
        path: PATHS.folders,
      },
    ],
  },
];

const projectRoute: RouteObject = {
  children: [
    {
      children: [
        {
          element: <Navigate to={`../${PATHS.translation}`} replace />,
          index: true,
        },
        {
          element: <ProjectPage />,
          path: ':projectId',
        },
      ],
      path: PATHS.project,
    },
  ],
};

const recordingRoute: RouteObject = {
  element: <RecordingPage />,
  path: `${PATHS.records}/:recordId`,
};

const adminRoutes: RouteObject[] = [
  {
    element: <Navigate to={PATHS.admin.customers} replace />,
    index: true,
  },
  {
    element: <Customers />,
    path: PATHS.admin.customers,
  },
  {
    element: <CustomersFeedback />,
    path: PATHS.admin.feedback,
  },
  {
    element: <AdminRecordings />,
    path: PATHS.admin.recordings,
  },
  {
    element: <AdminRecordingDetail />,
    path: PATHS.admin.recordingDetail,
  },
  {
    element: <AdminMLTasks />,
    path: PATHS.admin.mlTasks,
  },
  {
    element: <AdminOnscreenJobs />,
    path: PATHS.admin.onscreen,
  },
  {
    element: <AdminOnscreenJobDetail />,
    path: PATHS.admin.onscreenJob,
  },
  {
    element: <Survey />,
    path: PATHS.admin.survey,
  },
  {
    element: <NotFoundPage />,
    path: '*',
  },
];

const legacyRoutes: RouteObject[] = [
  {
    children: [
      {
        children: [
          {
            element: <Navigate to="../" replace />,
            index: true,
          },
          {
            element: <LegacyRouteBackwardCompatibility />,
            path: ':projectId',
          },
        ],
        path: PATHS.project,
      },
      {
        children: [
          {
            element: <LegacyRouteBackwardCompatibility />,
            path: PATHS.voices,
          },
          {
            element: <LegacyRouteBackwardCompatibility />,
            path: PATHS.glossaries,
          },
          {
            children: [
              {
                element: <Navigate to="../" replace />,
                index: true,
              },
              {
                element: <LegacyRouteBackwardCompatibility />,
                path: ':folderId',
              },
            ],
            path: PATHS.folders,
          },
        ],
        element: <Outlet />,
      },
    ],
    element: <Outlet />,
  },
];

const buildRoutes = (): RouteObject[] => {
  const routes: RouteObject[] = [
    {
      children: [
        {
          children: legacyRoutes,
          element: <Outlet />,
        },
        {
          children: [
            {
              children: mainRoutes,
              element: <MainLayout />,
            },
            projectRoute,
            recordingRoute,
            {
              element: <NotFoundPage />,
              path: '*',
            },
          ],
          Component: SpaceGuard,
          path: `${PATHS.spaces}/:spaceId`,
        },
        {
          element: <HomeRedirect />,
          path: PATHS.homepage,
        },
      ],
      element: <Outlet />,
    },
    {
      children: adminRoutes,
      element: <AdminPage />,
      path: PATHS.admin.homepage,
    },
  ];

  if (!IS_WHITE_LABEL) {
    routes.push(
      {
        element: <PricingPage />,
        path: PATHS.pricing,
      },
      {
        element: <AccountPage />,
        path: PATHS.account,
      },
      {
        element: <CheckoutPage />,
        path: PATHS.checkout,
      },
    );
  }

  return routes;
};

export const localizationAuthRoutes: RouteObject[] = [
  {
    children: buildRoutes(),
    element: <BaseLayout />,
  },
  {
    element: <NotFoundPage />,
    path: '*',
  },
];
